import { ref, computed, isRef, unref } from 'vue';
import { api } from '@/services/api';

/**
 * Category System Composable
 *
 * Manages the equipment category system including:
 * - Category metadata (icons, colors, labels)
 * - Field groups and grouping
 * - Completeness calculations (70% equipment / 30% devices)
 * - Automatic category detection based on BRICK class
 *
 * @param {Object|ComputedRef} equipmentInput - Equipment object or computed ref with id, brick_class, site_id
 * @returns {Object} Category system state and methods
 */
export function useCategorySystem(equipmentInput) {
  // Handle both direct objects and computed refs
  const getEquipment = () => unref(equipmentInput);
  // State
  const category = ref(null);
  const categoryFields = ref({});
  const categoryMetadata = ref({});
  const loading = ref(false);
  const error = ref(null);
  const deviceCompletenessValue = ref(0); // Tracks device documentation completeness

  // Category icon mapping
  const categoryIcons = {
    hvac_air_systems: 'air',
    hvac_hydronic_systems: 'water_drop',
    electrical_distribution: 'bolt',
    ddc_board: 'dashboard',
    controller: 'memory',
  };

  // Category color mapping
  const categoryColors = {
    hvac_air_systems: 'blue',
    hvac_hydronic_systems: 'cyan',
    electrical_distribution: 'amber',
    ddc_board: 'purple',
    controller: 'green',
  };

  // Computed Properties
  const categoryLabel = computed(() => {
    return categoryMetadata.value?.label || 'Unknown Category';
  });

  const categoryIcon = computed(() => {
    const categoryKey = category.value?.value || category.value;
    return categoryIcons[categoryKey] || 'category';
  });

  const categoryColor = computed(() => {
    const categoryKey = category.value?.value || category.value;
    return categoryColors[categoryKey] || 'grey';
  });

  /**
   * Get field groups organized by group key
   * Returns structure: { groupKey: { label, fields: {...} } }
   */
  const fieldGroups = computed(() => {
    return categoryFields.value || {};
  });

  /**
   * Calculate equipment completeness (0-1)
   * Based on required fields filled in category_data
   */
  const equipmentCompleteness = computed(() => {
    if (!categoryFields.value || Object.keys(categoryFields.value).length === 0) {
      return 0;
    }

    let totalRequired = 0;
    let filledRequired = 0;

    // Count required fields across all groups
    Object.values(categoryFields.value).forEach((group) => {
      if (group.fields) {
        Object.entries(group.fields).forEach(([fieldKey, fieldConfig]) => {
          if (fieldConfig.required) {
            totalRequired++;
            // Check if field has a value
            if (fieldConfig.value && fieldConfig.value !== '') {
              filledRequired++;
            }
          }
        });
      }
    });

    if (totalRequired === 0) {
      return 0;
    }

    return filledRequired / totalRequired;
  });

  /**
   * Device completeness (0-1)
   * Based on devices attached and their documentation
   * Updated via updateDeviceCompleteness() when device data changes
   */
  const deviceCompleteness = computed(() => deviceCompletenessValue.value);

  /**
   * Calculate overall completeness with weighting
   * 70% equipment + 30% devices
   */
  const overallCompleteness = computed(() => {
    return (equipmentCompleteness.value * 0.7) + (deviceCompleteness.value * 0.3);
  });

  /**
   * Get completeness color based on percentage
   */
  const completenessColor = computed(() => {
    const completion = overallCompleteness.value;
    if (completion >= 0.9) {return 'green';}
    if (completion >= 0.7) {return 'light-green';}
    if (completion >= 0.5) {return 'orange';}
    if (completion >= 0.3) {return 'deep-orange';}
    return 'red';
  });

  /**
   * Load category data from API
   */
  const loadCategoryData = async () => {
    const equipment = getEquipment();

    if (!equipment?.id) {
      error.value = 'Equipment ID is required';
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await api.get('/equipment-documentation/category-fields', {
        brick_class: equipment.brick_class,
      });


      // Set category first so computed properties can access it
      category.value = {
        value: response.category,
        label: response.category_label,
        has_programming: response.has_programming,
      };

      // Store field groups as returned from API
      categoryFields.value = response.field_groups || response.fields || {};

      // Now set metadata with computed values that depend on category
      categoryMetadata.value = {
        label: response.category_label,
        icon: categoryIcon.value,
        color: categoryColor.value,
      };


    } catch (err) {
      error.value = err.message || 'Failed to load category data';
      console.error('[useCategorySystem] Failed to load category data:', err);
    } finally {
      loading.value = false;
    }
  };


  /**
   * Count total fields in a group
   */
  const totalFieldsInGroup = (groupKey) => {
    const group = categoryFields.value[groupKey];
    if (!group || !group.fields) {
      return 0;
    }
    return Object.keys(group.fields).length;
  };

  /**
   * Count completed fields in a group
   */
  const completedFieldsInGroup = (groupKey) => {
    const group = categoryFields.value[groupKey];
    if (!group || !group.fields) {
      return 0;
    }

    let completed = 0;
    Object.values(group.fields).forEach((field) => {
      if (field.value && field.value !== '') {
        completed++;
      }
    });

    return completed;
  };

  /**
   * Update device completeness from parent
   * Call this when device data changes
   *
   * Scoring per device:
   * - name: 20%
   * - brick_class: 20%
   * - device_part_id OR (manufacturer + model): 30%
   * - points with at least 1 point: 30%
   */
  const updateDeviceCompleteness = (deviceData) => {
    if (!deviceData || deviceData.length === 0) {
      deviceCompletenessValue.value = 0;
      return;
    }

    let totalScore = 0;

    deviceData.forEach((device) => {
      let deviceScore = 0;

      // Name: 20%
      if (device.name && device.name.trim() !== '') {
        deviceScore += 0.2;
      }

      // BRICK class: 20%
      if (device.brick_class && device.brick_class.trim() !== '') {
        deviceScore += 0.2;
      }

      // Device part OR manufacturer/model: 30%
      if (device.device_part_id) {
        deviceScore += 0.3;
      } else if (device.manufacturer && device.model) {
        deviceScore += 0.3;
      } else if (device.manufacturer || device.model) {
        deviceScore += 0.15; // Partial credit
      }

      // Points: 30%
      if (device.points && device.points.length > 0) {
        deviceScore += 0.3;
      }

      totalScore += deviceScore;
    });

    // Average score across all devices
    deviceCompletenessValue.value = totalScore / deviceData.length;
  };

  return {
    // State
    category,
    categoryFields,
    categoryMetadata,
    loading,
    error,

    // Computed
    categoryLabel,
    categoryIcon,
    categoryColor,
    fieldGroups,
    equipmentCompleteness,
    deviceCompleteness,
    overallCompleteness,
    completenessColor,

    // Methods
    loadCategoryData,
    totalFieldsInGroup,
    completedFieldsInGroup,
    updateDeviceCompleteness,
  };
}

export default useCategorySystem;
